import { Tooltip } from "@indigo-components";
// Raw antd `Popover` — no `@indigo-components` popover primitive exists yet
// (same flagged gap as QuestionPhrasingPopover, the house pattern this
// follows). It portals to body and collision-flips, which the hand-rolled
// absolute-positioned gallery it replaced could not: inside the floating
// panel (overflow: hidden, user-resizable to 300px) the 296px gallery
// clipped unreachable.
import { Popover } from "antd";
import { useAtom } from "jotai";
import {
  FC,
  KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { indyGlassesVariantAtom } from "~/atoms/chatbot";
import "./IndyGlassesPicker.css";
import { glassesById, INDY_GLASSES } from "./indyGlasses";

/**
 * Header control that lets each user pick which hand-drawn glasses their Indy
 * launcher wears. The trigger shows the current pick; the popover is a live
 * gallery of every variant. Selection persists per-browser via
 * `indyGlassesVariantAtom`, so the launcher (and this control) update instantly.
 */
export const IndyGlassesPicker: FC = () => {
  const [variant, setVariant] = useAtom(indyGlassesVariantAtom);
  const [open, setOpen] = useState(false);
  const current = glassesById(variant);

  const triggerRef = useRef<HTMLButtonElement>(null);

  // antd Popover dismisses on outside click but not on Escape, and its
  // body-portal is at the end of the tab order — so both keyboard concerns are
  // handled here, scoped to FOCUS rather than to a window listener. A window
  // listener claimed every Escape while the gallery was open, which inverted
  // layering: a slash menu or rename field opened afterwards lost its Escape
  // to a background gallery. Focus-scoped, the surface the user is in wins,
  // and the gallery only answers for keys aimed at it.
  //
  // stopPropagation on the React event also halts the NATIVE bubble past the
  // root, so closing the gallery can't double-fire /indy's window-level
  // Esc-to-exit (the same guard ChatComposer's slash menu uses).
  const closeAndRefocus = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);
  const handleGalleryKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      closeAndRefocus();
    },
    [closeAndRefocus],
  );
  const handleTriggerKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (e.key !== "Escape" || !open) return;
      e.stopPropagation();
      setOpen(false);
    },
    [open],
  );

  // The portal renders at the end of document.body, so without moving focus a
  // keyboard user's next Tab lands on the header control after the trigger —
  // the swatches would be focusable but unreachable. Focus the current pick
  // once the overlay exists. A short retry loop, not antd's afterOpenChange:
  // that callback fires at motion END, so it is late by the zoom duration and
  // never fires at all where animations are paused (hidden tabs).
  useEffect(() => {
    if (!open) return;
    let tries = 0;
    let t: ReturnType<typeof setTimeout> | undefined;
    const tryFocus = () => {
      const pop = document.querySelector(".indy-gp-pop");
      const target =
        pop?.querySelector<HTMLButtonElement>(
          '.indy-gp__swatch[aria-checked="true"]',
        ) ?? pop?.querySelector<HTMLButtonElement>(".indy-gp__swatch");
      if (target) {
        target.focus();
        return;
      }
      if (++tries < 10) t = setTimeout(tryFocus, 30);
    };
    tryFocus();
    return () => clearTimeout(t);
  }, [open]);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      overlayClassName="indy-gp-pop"
      content={
        <div
          role="menu"
          aria-label="Indy glasses"
          className="indy-gp__body"
          // The floating panel's title bar is a drag handle, and a React
          // portal still bubbles synthetic events through the component
          // tree — without this, mousedown anywhere in the gallery drags the
          // whole panel. This div also owns the 14px padding (antd's inner
          // container is zeroed in the CSS): padding on `.ant-popover-inner`
          // sat OUTSIDE this guard, so a drag starting on that ring moved the
          // panel while the body-portalled gallery stayed anchored.
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={handleGalleryKeyDown}
        >
          <div className="indy-gp__title">Choose your Indy</div>
          <div className="indy-gp__grid">
            {INDY_GLASSES.map((g) => (
              <button
                key={g.id}
                type="button"
                role="menuitemradio"
                aria-checked={g.id === variant}
                className={`indy-gp__swatch${g.id === variant ? " is-selected" : ""}`}
                onClick={() => {
                  setVariant(g.id);
                  // Return focus to the trigger — for a keyboard user the
                  // portal otherwise strands focus on an unmounting node.
                  closeAndRefocus();
                }}
              >
                <span className="indy-gp__chip">
                  <img src={g.src} alt="" />
                </span>
                <span className="indy-gp__label">{g.label}</span>
              </button>
            ))}
          </div>
        </div>
      }
    >
      {/* Plain span seam: Tippy (the design-system Tooltip) needs a real
            DOM child for its ref, and antd Popover's cloned-child handlers
            need a DOM node too — neither can wrap the other directly. The
            popover's click trigger attaches here; the tooltip attaches to
            the button inside. */}
      <span className="indy-gp">
        <Tooltip
          content="Choose Indy's glasses"
          placement="top"
          // mouseenter only (not the default mouseenter+focus): focus
          // returning to the trigger when the gallery closes would reopen
          // a focus-triggered tooltip with no pointer near it. Tippy also
          // hides on click, and `disabled` keeps it away while the gallery
          // is up. The aria-label keeps the button named for keyboard and
          // screen-reader users.
          trigger="mouseenter"
          disabled={open}
        >
          <button
            ref={triggerRef}
            type="button"
            className="indy-gp__trigger"
            aria-haspopup="true"
            aria-expanded={open}
            aria-label="Choose Indy's glasses"
            onKeyDown={handleTriggerKeyDown}
          >
            <span className="indy-gp__chip">
              <img src={current.src} alt="" />
            </span>
          </button>
        </Tooltip>
      </span>
    </Popover>
  );
};

export default IndyGlassesPicker;
